import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, Button, TextField, Chip, Alert } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import StyledDrawer from "../../shared/UIParts/StyledDrawer";

import { formatProductType } from "../../utils/enumFormatter";
import { colorHandlerCode, colorProductType } from "../../utils/colorEnumFormatter";
import { ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatHandlerCode } from "../../utils/namesFormater";
import TransferSourceInfo from "./TransferSourceInfo";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { createContactColorMap } from "../../utils/contactColorMapping";
import { applyFundsSumToMimshak } from "../../hooks/feeLab/useNewProductHandlers";
import SplitProductSection from "./SplitProductSection";

interface Props {
  open: boolean;
  onClose: () => void;
  mimshak: MimshakData;
  contacts?: Contact[];
  onDelete: (mimshak: MimshakData) => void;
  onUpdate: (updates: Partial<MimshakData>) => void;
  onSplitProduct?: (sourceMimshak: MimshakData, splitAmounts: number[]) => void;
  onDepositChange?: (policyNumber: string, amount: number) => void;
  onRecurrentChange?: (policyNumber: string, amount: number | null) => void;
  currentDeposit?: number;
  currentRecurrent?: number | null;
}

const NewProductTypeDrawer: React.FC<Props> = ({ open, mimshak, contacts = [], onClose, onDelete, onUpdate, onSplitProduct, onDepositChange, onRecurrentChange, currentDeposit = 0, currentRecurrent = 0 }) => {
  const [savingFeeInput, setSavingFeeInput] = useState(mimshak.saving_fee?.toString() ?? "");
  const productTypes = [
    ProductType.EDUCATION_FUND,
    ProductType.PROVIDENT_FUND,
    ProductType.INVESTMENT_PROVIDENT_FUND,
    ProductType.PURE_SAVINGS_POLICY,
  ];

  const contactColorMap = createContactColorMap(contacts.map(c => c.id));
  const showContactPicker = contacts.length > 1;

  useEffect(() => {
    setSavingFeeInput(mimshak.saving_fee?.toString() ?? "");
  }, [mimshak.saving_fee]);

  useEffect(() => {
    if (mimshak.product_type != null && productTypes.includes(mimshak.product_type as ProductType)) {
      onUpdate({ policy_number: mimshak.policy_number, product_type: mimshak.product_type });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimshak.policy_number]);

  const handleChangeType = (type: number) => {
    onUpdate({ policy_number: mimshak.policy_number, product_type: type });
  };
  const handleFeeBlur = () => {
    const parsed = parseFloat(savingFeeInput.replace(",", "."));
    onUpdate({
      policy_number: mimshak.policy_number,
      saving_fee: isNaN(parsed) ? null : parsed,
    });
  };
  const handleChangeSum = (val: number) => {
    const updated = applyFundsSumToMimshak(mimshak, val);
    const isDeposit = (mimshak.deposit_amount ?? 0) > 0;
    onUpdate({
      policy_number: mimshak.policy_number,
      funds_sum: val,
      data: updated.data,
      ...(isDeposit ? { deposit_amount: val } : {}),
    });
  };
  const handleChangeRecurrentDeposit = (val: number) => {
    onUpdate({ policy_number: mimshak.policy_number, recurrent_deposit: val });
    onRecurrentChange?.(mimshak.policy_number, val);
  };

  const handleChangeDeposit = (val: number) => {
    onDepositChange?.(mimshak.policy_number, val);
  };
  const handleSelectContact = (contactId: number) => {
    onUpdate({ policy_number: mimshak.policy_number, contact_id: contactId });
  };

  const [triedToClose, setTriedToClose] = useState(false);
  const hasProductType = mimshak.product_type != null && productTypes.includes(mimshak.product_type as ProductType);

  const handleClose = () => {
    if (!hasProductType) { setTriedToClose(true); return; }
    onClose();
  };

  return (
    <StyledDrawer anchor="left" open={open} onClose={handleClose} onBack={handleClose} width={320}>
      <Box sx={{ p: 3, direction: "rtl" }}>
        <Typography variant="h6" gutterBottom>בחר סוג מוצר</Typography>
        {triedToClose && !hasProductType && (
          <Alert severity="error" sx={{ mb: 2 }}>יש לבחור סוג מוצר</Alert>
        )}
        <Box display="flex" alignItems="center" mb={2}>
          <Box width={12} height={12} borderRadius="30%" bgcolor={colorHandlerCode(mimshak.handler_code)} mr={1} />
          <Typography variant="h6" fontWeight="bold">
            &nbsp; {formatHandlerCode(mimshak.handler_code)}
          </Typography>
        </Box>

        {showContactPicker && (
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>לקוח:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {contacts.map((c) => {
                const color = contactColorMap[c.id];
                const isSelected = mimshak.contact_id === c.id;
                return (
                  <Chip
                    key={c.id}
                    label={`${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || String(c.id)}
                    onClick={() => handleSelectContact(c.id)}
                    variant={isSelected ? "filled" : "outlined"}
                    icon={isSelected ? <CheckIcon fontSize="small" /> : undefined}
                    sx={{
                      borderColor: color,
                      backgroundColor: isSelected ? `${color}99` : undefined,
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        <Stack spacing={2}>
          {productTypes.map((type) => (
            <Chip
              key={type}
              label={formatProductType(type)}
              color="default"
              variant={mimshak.product_type === type ? "filled" : "outlined"}
              onClick={() => handleChangeType(type)}
              sx={{
                borderColor: colorProductType(type),
                backgroundColor:
                  mimshak.product_type === type ? `${colorProductType(type)}66` : undefined,
              }}
              icon={
                mimshak.product_type === type ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : undefined
              }
            />
          ))}
        </Stack>

        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>סכום:</Typography>
          <CurrencyTextField
            label="סכום"
            value={mimshak.funds_sum ?? 0}
            onChange={handleChangeSum}
            fullWidth
            sx={{ maxWidth: "100%" }}
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>דמי ניהול:</Typography>
          <TextField
            label="דמי ניהול (%)"
            value={savingFeeInput}
            onChange={(e) => setSavingFeeInput(e.target.value)}
            onBlur={handleFeeBlur}
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>הפקדה חד-פעמית:</Typography>
          <CurrencyTextField
            label="הפקדה חד-פעמית"
            value={currentDeposit}
            onChange={handleChangeDeposit}
            fullWidth
            sx={{ maxWidth: "100%" }}
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>הוראת קבע חודשית:</Typography>
          <CurrencyTextField
            label="הוראת קבע חודשית"
            value={currentRecurrent || (mimshak.recurrent_deposit ?? 0)}
            onChange={handleChangeRecurrentDeposit}
            fullWidth
            sx={{ maxWidth: "100%" }}
          />
        </Box>

        {onSplitProduct && (
          <SplitProductSection mimshak={mimshak} onSplitProduct={(src, amounts) => { onSplitProduct(src, amounts); onClose(); }} />
        )}

        <TransferSourceInfo mimshak={mimshak} />

        <Button
          variant="outlined"
          color="error"
          sx={{ mt: 6 }}
          onClick={() => onDelete(mimshak)}
        >
          מחק מוצר
        </Button>
      </Box>
    </StyledDrawer>
  );
};

export default NewProductTypeDrawer;
